import axios from 'axios';

class CartService {
  constructor() {
    this.syncInProgress = false;
    this.lastSyncTime = null;
    this.syncInterval = null;
    this.STORAGE_KEY = 'checkoutCartItems';
    this.LAST_SYNC_KEY = 'lastCartSync';
    this.SYNC_INTERVAL_MS = 30000; // Sync every 30 seconds
  }

  // Start automatic cart synchronization
  startAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    // Sync immediately when starting
    this.syncCart();

    // Set up periodic sync
    this.syncInterval = setInterval(() => {
      this.syncCart();
    }, this.SYNC_INTERVAL_MS);
  }

  // Stop automatic cart synchronization
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Manual cart synchronization
  async syncCart(forceSync = false) {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user'));
    
    if (!token || !user || this.syncInProgress) {
      return;
    }

    this.syncInProgress = true;

    try {
      // Get the saved cart from the database
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/shoppingCart/sync/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const dbCart = response.data.saved_cart || [];
      const localCart = JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];

      // Compare the carts - if they're different, update localStorage
      if (forceSync || !this.areCartsEqual(localCart, dbCart)) {
        console.log('Cart sync: Database cart differs from local cart, updating localStorage');
        
        // Update localStorage with database cart
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dbCart));
        
        // Store sync timestamp
        localStorage.setItem(this.LAST_SYNC_KEY, response.data.timestamp);
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('cart-synced', { 
          detail: { 
            cart: dbCart,
            source: 'database',
            timestamp: response.data.timestamp
          }
        }));

        console.log('Cart synchronized with database');
      } else {
        console.log('Cart sync: Local cart is up to date');
      }

      this.lastSyncTime = new Date();
      
    } catch (error) {
      console.error('Error syncing cart:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  // Compare two cart arrays to see if they're equal
  areCartsEqual(cart1, cart2) {
    if (cart1.length !== cart2.length) {
      return false;
    }

    // Sort both carts by giftCardId and optionId for comparison
    const sortCart = (cart) => [...cart].sort((a, b) => {
      if (a.giftCardId !== b.giftCardId) return a.giftCardId.localeCompare(b.giftCardId);
      return (a.optionId || 0) - (b.optionId || 0);
    });

    const sorted1 = sortCart(cart1);
    const sorted2 = sortCart(cart2);

    return sorted1.every((item1, index) => {
      const item2 = sorted2[index];
      return (
        item1.giftCardId === item2.giftCardId &&
        item1.optionId === item2.optionId &&
        item1.quantity === item2.quantity &&
        item1.name === item2.name
      );
    });
  }

  // Force refresh cart from database
  async forceRefreshCart() {
    return this.syncCart(true);
  }

  // Get local cart
  getLocalCart() {
    return JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];
  }

  // Update local cart and optionally sync with database
  updateLocalCart(cart, syncToDb = true) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cart));
    
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('cart-updated', { 
      detail: { 
        cart: cart,
        source: 'local'
      }
    }));

    // If user is logged in and we want to sync to database
    if (syncToDb) {
      this.syncToDatabase(cart);
    }
  }

  // Sync local cart to database
  async syncToDatabase(cart) {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user'));
    
    if (!token || !user) {
      return;
    }

    try {
      // This would require a new backend endpoint to update the entire cart
      await axios.put(`${process.env.VUE_APP_API_URL}/api/shoppingCart/update-cart/${user.id}`, {
        cart: cart
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Error syncing cart to database:', error);
    }
  }

  // Get last sync time
  getLastSyncTime() {
    const lastSync = localStorage.getItem(this.LAST_SYNC_KEY);
    return lastSync ? new Date(lastSync) : null;
  }

  // Check if cart needs refresh (based on time since last sync)
  needsRefresh() {
    const lastSync = this.getLastSyncTime();
    if (!lastSync) return true;
    
    const now = new Date();
    const timeDiff = now - lastSync;
    const REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes
    
    return timeDiff > REFRESH_THRESHOLD;
  }
}

// Create singleton instance
const cartService = new CartService();

export default cartService;